import storageService from '../storageService';
import deviceConnectionStatusService from './deviceConnectionStatusService';

/**
 * Device Kit Helpers — bidirectional holder ↔ charger linking.
 *
 * Chargers and holders form kits (one-to-one pairing). The kit link is stored
 * in two directions:
 *
 *   holder.lastChargerDusn
 *     Holder stores its charger's DUSN.
 *     ILUMA i2 (UAM): set via registration or asset fetch.
 *     ILUMA i5 (UAMF): set via registration, holder connectivity status (connectedThrough),
 *                      or HOLDER_INSERTED WebSocket event;
 *                      cleared via HOLDER_EXTRACTED WebSocket event.
 *
 *   charger.holderDusn
 *     Charger stores its holder's DUSN.
 *     ILUMA i2 (UAM): set via registration.
 *     ILUMA i5 (UAMF): set via registration, charger connectivity status (dependantDevice),
 *                      or HOLDER_INSERTED WebSocket event;
 *                      cleared via HOLDER_EXTRACTED WebSocket event.
 *
 * When both links resolve to holders, the selector applies a priority rule:
 *   1. Exactly one connected → return it
 *   2. Multiple connected    → prefer first lastChargerDusn match
 *   3. None connected        → prefer first lastChargerDusn match
 *
 * With no links in state at all, two offline fallbacks run in order:
 *   1. localStorage kit map (chargerDusn → holderDusn), written from live connectivity data
 *   2. the only registered holder whose type matches charger.holderType;
 *      0 or 2+ matches → holder: null
 *
 * Both exist because i5 (UAMF) kit links are populated only by live sources — a
 * disconnected i5 kit after an app reload has no link at all, while the registered
 * holder is already in the device list. The cached map is an observed pairing, so it
 * wins; the type match is a guess constrained to be unambiguous, since serial numbers
 * encode the device family (type), not kit membership — with several same-family
 * holders the pairing is unknowable offline and the fallback declines rather than guesses.
 */

const resolveHolderByKitType = (deviceList, charger) => {
    if (!charger.holderType) return null;

    const typeMatches = Object.values(deviceList).filter(
        (device) => device?.serialNumber && device?.type === charger.holderType
    );

    return typeMatches.length === 1 ? typeMatches[0] : null;
};

const resolveDeviceKit = (deviceList, chargerDusn) => {
    const charger = deviceList?.[chargerDusn];

    if (!charger) {
        return {charger: null, holder: null};
    }

    // ── Collect holder candidates from both directional links ──────────

    // Link 1: holders pointing to this charger via lastChargerDusn
    const holdersFromLastChargerLink = Object.values(deviceList).filter(
        (device) => device?.lastChargerDusn === chargerDusn
    );

    // Link 2: holder this charger points to via holderDusn
    const holderFromChargerLink = charger.holderDusn ? deviceList[charger.holderDusn] : undefined;

    const hasKitLinksInState = holdersFromLastChargerLink.length > 0 || holderFromChargerLink;

    // ── Fallback: restore holder from localStorage kit map (i5 disconnected case) ──
    if (!hasKitLinksInState) {
        const cachedHolderDusn = storageService.getDeviceKitMap()[chargerDusn];
        const cachedHolder = deviceList?.[cachedHolderDusn];

        return {charger, holder: cachedHolder ?? resolveHolderByKitType(deviceList, charger)};
    }

    // ── Deduplicate by serialNumber, track which link found each ───────

    const candidateMap = new Map();

    for (const device of holdersFromLastChargerLink) {
        if (device?.serialNumber) {
            candidateMap.set(device.serialNumber, {device, preferredLink: true});
        }
    }

    if (holderFromChargerLink?.serialNumber && !candidateMap.has(holderFromChargerLink.serialNumber)) {
        candidateMap.set(holderFromChargerLink.serialNumber, {
            device: holderFromChargerLink,
            preferredLink: false,
        });
    }

    if (candidateMap.size === 0) {
        return {charger, holder: resolveHolderByKitType(deviceList, charger)};
    }

    // ── Pick the best candidate using connection status + tiebreaker ───

    const allCandidates = Array.from(candidateMap.values());
    const isConnected = ({device}) => deviceConnectionStatusService.isDeviceConnected(device?.connectionStatus);
    const connected = allCandidates.filter(isConnected);

    const selectPreferred = (candidates) => {
        const preferred = candidates.find(({preferredLink}) => preferredLink);

        return (preferred || candidates[0]).device;
    };

    // Exactly one connected — return it regardless of which link found it
    if (connected.length === 1) {
        return {charger, holder: connected[0].device};
    }

    // Multiple connected (or none connected) — prefer lastChargerDusn match
    if (connected.length > 1) {
        return {charger, holder: selectPreferred(connected)};
    }

    return {charger, holder: selectPreferred(allCandidates)};
};

export default {resolveDeviceKit};
